import ImagePreviewDialog from "@/core/components/ImagePreviewDialog";
import { useProductAutocomplete } from "@/features/product/usecases/useProductAutocomplete";
import {
	DndContext,
	type DragEndEvent,
	KeyboardSensor,
	PointerSensor,
	closestCenter,
	useSensor,
	useSensors,
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
	Autocomplete,
	type AutocompleteFetchParams,
	type AutocompleteFetcher,
	Badge,
	Button,
	Can,
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
	Input,
	ScrollArea,
	Separator,
	Skeleton,
	toast,
	useAuth,
	useDialogController,
} from "@k3-tech/react-kit";
import { Link } from "@tanstack/react-router";
import {
	BanIcon,
	ChevronDownIcon,
	ChevronsDownUpIcon,
	ChevronsUpDownIcon,
	EditIcon,
	EllipsisVerticalIcon,
	LayersIcon,
	ListIcon,
	ListTreeIcon,
	PlusIcon,
	RefreshCwIcon,
	RotateCcwIcon,
	SearchIcon,
	TableIcon,
	Trash2Icon,
} from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import {
	resolveMenuImagePrefill,
	resolveMenuImagePreview,
	resolveMenuImageType,
} from "../usecases/resolveMenuImageType";
import { useBranchProductSalesChannelItemList } from "../usecases/useBranchProductSalesChannelItemList";
import {
	BRANCH_PRODUCT_ITEM_TYPE_EXCLUDE,
	useBranchProductSalesChannelItemMutations,
} from "../usecases/useBranchProductSalesChannelItemMutations";
import { useMenuGroupBindings } from "../usecases/useMenuGroupBindings";
import { useMenuGroupBranchAutocomplete } from "../usecases/useMenuGroupBranchAutocomplete";
import type { SalesChannelCategoryGroupProductRow } from "../usecases/useSalesChannelCategoryGroupProductList";
import { useSalesChannelCategoryGroupProductMutations } from "../usecases/useSalesChannelCategoryGroupProductMutations";
import {
	type SalesChannelCategoryTreeNode,
	useSalesChannelCategoryGroupTree,
} from "../usecases/useSalesChannelCategoryGroupTree";
import type { SalesChannelCategoryRow } from "../usecases/useSalesChannelCategoryList";
import { useSalesChannelCategoryMutations } from "../usecases/useSalesChannelCategoryMutations";
import { GroupProductAddonList } from "./GroupProductAddonList";
import {
	type CategoryFormValues,
	SalesChannelCategoryFormDialog,
} from "./SalesChannelCategoryFormDialog";
import {
	type GroupProductFormValues,
	SalesChannelGroupProductFormDialog,
} from "./SalesChannelGroupProductFormDialog";
import {
	UnavailableItemDialog,
	type UnavailableItemFormValues,
} from "./UnavailableItemDialog";

// Above this many categories the tree starts collapsed (roots only) to keep the
// initial render light; below it everything is auto-expanded.
const AUTO_EXPAND_LIMIT = 40;

type CategoryDialogState =
	| { mode: "create" }
	| { mode: "edit"; row: SalesChannelCategoryRow }
	| null;

type ProductDialogState =
	| { mode: "create"; categoryID?: string }
	| { mode: "edit"; row: SalesChannelCategoryGroupProductRow }
	| null;

const plural = (n: number, one: string, many = `${one}s`) =>
	`${n} ${n === 1 ? one : many}`;

const productLabel = (row: SalesChannelCategoryGroupProductRow) =>
	row.product?.productName || row.productName || "-";
const productCode = (row: SalesChannelCategoryGroupProductRow) =>
	row.product?.productCode || row.productCode || row.barcode || "";

const productMatches = (
	product: SalesChannelCategoryGroupProductRow,
	q: string,
): boolean =>
	`${productLabel(product)} ${productCode(product)}`.toLowerCase().includes(q);

const CategoryStatusBadge = ({
	category,
	productCount,
}: {
	category: SalesChannelCategoryRow;
	productCount: number;
}) => {
	if (!category.isInMenu) return <Badge variant="secondary">Hidden</Badge>;
	if (productCount === 0)
		return (
			<Badge variant="secondary" className="text-amber-600">
				Empty · skipped
			</Badge>
		);
	return (
		<Badge variant="secondary" className="text-green-600">
			In menu
		</Badge>
	);
};

type ProductAvailability =
	| "available"
	| "outOfStock"
	| "noPrice"
	| "notSellable"
	| "hidden"
	| "inactive";

// Left-stripe accent and subtle row tint per effective availability, so the tree mirrors the Grab menu at a glance.
const availabilityRowClass: Record<ProductAvailability, string> = {
	available: "border-l-emerald-400",
	outOfStock: "border-l-amber-400 bg-amber-50/40",
	noPrice: "border-l-violet-400 bg-violet-50/40",
	notSellable: "border-l-orange-400 bg-orange-50/40",
	hidden: "border-l-rose-400 bg-rose-50/40",
	inactive: "border-l-slate-300 bg-slate-50/40",
};

const AvailabilityStatus = ({ status }: { status: ProductAvailability }) => {
	const map: Record<ProductAvailability, { label: string; cls: string }> = {
		available: { label: "Available", cls: "text-emerald-600" },
		outOfStock: { label: "Out of stock", cls: "text-amber-600" },
		noPrice: { label: "No price", cls: "text-violet-600" },
		notSellable: { label: "Not sellable", cls: "text-orange-600" },
		hidden: { label: "Hidden", cls: "text-rose-600" },
		inactive: { label: "Inactive", cls: "text-slate-400" },
	};
	const { label, cls } = map[status];
	return (
		<span
			className={`inline-flex shrink-0 items-center gap-1.5 text-[11px] font-semibold ${cls}`}
		>
			<span className="h-1.5 w-1.5 rounded-full bg-current" />
			{label}
		</span>
	);
};

const ProductRow = ({
	product,
	showQty,
	canMarkUnavailable,
	isExcluded,
	canReorder,
	onEdit,
	onDelete,
	onMarkUnavailable,
}: {
	product: SalesChannelCategoryGroupProductRow;
	showQty: boolean;
	canMarkUnavailable: boolean;
	isExcluded: boolean;
	canReorder: boolean;
	onEdit: (row: SalesChannelCategoryGroupProductRow) => void;
	onDelete: (row: SalesChannelCategoryGroupProductRow) => void;
	onMarkUnavailable: (row: SalesChannelCategoryGroupProductRow) => void;
}) => {
	const [addonOpen, setAddonOpen] = useState(false);
	const [previewOpen, setPreviewOpen] = useState(false);
	const hasAddon = !!product.product?.hasAddon;
	const previewURL = resolveMenuImagePreview(product);
	const uomName = product.product?.baseUom?.uomName ?? "pcs";
	// showQty is wired to Boolean(branchID); availability, qty and the stripe are per-branch,
	// so without a selected branch we show only the menu template (no status/qty/stripe).
	const branchSelected = showQty;
	const qtyLabel =
		branchSelected && (product.qty ?? 0) > 0
			? ` · ${Number(product.qty).toLocaleString("id-ID")} ${uomName}`
			: "";

	// A manual hide outranks the auto reasons; a missing channel price outranks out-of-stock,
	// since no price blocks selling regardless of stock. All read as unavailable on Grab.
	// Null when no branch is selected: availability is branch-scoped and would be ambiguous.
	// A zero channel price can mean two different things: the master product is turned off
	// (not active / not sellable), or it is sellable but simply has no price set yet.
	const isNotSellableMaster =
		product.product?.canBeSold === false || product.product?.isActive === false;
	const availability: ProductAvailability | null = !branchSelected
		? null
		: product.isActive === false
			? "inactive"
			: isExcluded
				? "hidden"
				: product.isNoPrice
					? isNotSellableMaster
						? "notSellable"
						: "noPrice"
					: product.isOutOfStock
						? "outOfStock"
						: "available";

	// Manual hide/restore only applies to an available or already-hidden product; an auto out-of-stock
	// (or inactive) item is unavailable already, so the toggle would just add noise.
	const canToggleAvailability =
		branchSelected && (isExcluded || availability === "available");

	const {
		attributes,
		listeners,
		setNodeRef,
		transform,
		transition,
		isDragging,
	} = useSortable({
		id: product.salesChannelCategoryGroupProductID,
		disabled: !canReorder,
	});
	const sortableStyle = {
		transform: CSS.Transform.toString(transform),
		transition,
		...(isDragging ? { opacity: 0.5, zIndex: 1 } : {}),
	};

	return (
		<div ref={setNodeRef} style={sortableStyle}>
			<div
				className={`group flex items-start gap-2.5 border-l-[3px] py-2 pr-2 pl-[27px] transition-colors hover:bg-muted/50 ${
					availability
						? availabilityRowClass[availability]
						: "border-l-transparent"
				} ${canReorder ? "cursor-grab active:cursor-grabbing" : ""}`}
				{...(canReorder ? attributes : {})}
				{...(canReorder ? listeners : {})}
			>
				{previewURL ? (
					<button
						type="button"
						aria-label="Preview image"
						className="h-[72px] w-[72px] shrink-0 overflow-hidden rounded-xl bg-slate-100"
						onPointerDown={(e) => e.stopPropagation()}
						onClick={(e) => {
							e.stopPropagation();
							setPreviewOpen(true);
						}}
					>
						<img
							src={previewURL}
							alt=""
							className="h-full w-full cursor-zoom-in object-cover"
						/>
					</button>
				) : (
					<div className="h-[72px] w-[72px] shrink-0 rounded-xl bg-slate-100" />
				)}
				<div className="flex min-w-0 flex-1 flex-col gap-0.5">
					<div className="truncate font-medium text-sm">
						{productLabel(product)}
					</div>
					<div className="truncate text-muted-foreground text-xs tabular-nums">
						{productCode(product)}
						{qtyLabel}
					</div>
					{hasAddon ? (
						<Button
							type="button"
							variant="ghost"
							size="sm"
							className="-ml-1.5 h-6 gap-1 self-start px-1.5 text-[11px] text-teal-600"
							title="Add-ons"
							onClick={() => setAddonOpen((open) => !open)}
						>
							<LayersIcon className="h-3.5 w-3.5" />
							{product.product?.addonGroup?.name || "Add-ons"}
							<ChevronDownIcon
								className={`h-3 w-3 transition-transform ${
									addonOpen ? "" : "-rotate-90"
								}`}
							/>
						</Button>
					) : null}
					<div className="mt-1 flex items-center gap-2">
						{availability ? <AvailabilityStatus status={availability} /> : null}
						{availability === "noPrice" ? (
							<Link
								to="/admin/pricelist"
								className="font-medium text-[11px] text-violet-600 underline-offset-2 hover:underline"
								onPointerDown={(e) => e.stopPropagation()}
								onClick={(e) => e.stopPropagation()}
							>
								Set price
							</Link>
						) : null}
						{availability === "notSellable" ? (
							<Link
								to="/admin/product/$id/detail"
								params={{ id: product.productID }}
								className="font-medium text-[11px] text-orange-600 underline-offset-2 hover:underline"
								onPointerDown={(e) => e.stopPropagation()}
								onClick={(e) => e.stopPropagation()}
							>
								Enable selling
							</Link>
						) : null}
						{product.priceType === "custom" ? (
							<Badge
								variant="secondary"
								className="h-5 px-1.5 font-normal text-[11px] text-teal-600"
							>
								{`Custom · Rp ${Number(product.customPrice).toLocaleString("id-ID")}`}
							</Badge>
						) : null}
						<DropdownMenu>
							<DropdownMenuTrigger asChild>
								<Button
									type="button"
									variant="ghost"
									size="sm"
									className="ml-auto h-7 w-7 shrink-0 p-0"
									aria-label="Product actions"
									onPointerDown={(e) => e.stopPropagation()}
								>
									<EllipsisVerticalIcon className="h-4 w-4" />
								</Button>
							</DropdownMenuTrigger>
							<DropdownMenuContent align="end">
								{canToggleAvailability ? (
									<Can permissions="sales_channel_category_groups.edit">
										<DropdownMenuItem
											disabled={!canMarkUnavailable}
											onClick={() => onMarkUnavailable(product)}
										>
											{isExcluded ? (
												<>
													<RotateCcwIcon className="h-4 w-4" />
													Restore to menu
												</>
											) : (
												<>
													<BanIcon className="h-4 w-4" />
													Mark unavailable
												</>
											)}
										</DropdownMenuItem>
									</Can>
								) : null}
								<Can permissions="sales_channel_category_groups.edit">
									<DropdownMenuItem onClick={() => onEdit(product)}>
										<EditIcon className="h-4 w-4" />
										Edit
									</DropdownMenuItem>
								</Can>
								<Can permissions="sales_channel_category_groups.delete">
									<DropdownMenuItem
										className="text-destructive"
										onClick={() => onDelete(product)}
									>
										<Trash2Icon className="h-4 w-4" />
										Delete
									</DropdownMenuItem>
								</Can>
							</DropdownMenuContent>
						</DropdownMenu>
					</div>
				</div>
			</div>
			{hasAddon && addonOpen ? (
				<div className="mr-2 mb-2 ml-[46px]">
					<GroupProductAddonList
						addonGroupID={product.product?.addonGroupID}
						compact
					/>
				</div>
			) : null}
			<ImagePreviewDialog
				open={previewOpen}
				onOpenChange={setPreviewOpen}
				src={previewURL}
				alt="Menu image preview"
				title="Menu image preview"
			/>
		</div>
	);
};

const CategoryRow = ({
	node,
	isOpen,
	showQty,
	onToggle,
	onAddProduct,
	onEditCategory,
	onDeleteCategory,
	onEditProduct,
	onDeleteProduct,
	canMarkUnavailable,
	onMarkUnavailable,
	excludedProductIDs,
	canReorder,
	onReorderProducts,
}: {
	node: SalesChannelCategoryTreeNode;
	isOpen: boolean;
	showQty: boolean;
	canMarkUnavailable: boolean;
	onMarkUnavailable: (row: SalesChannelCategoryGroupProductRow) => void;
	excludedProductIDs: Set<string>;
	onToggle: (id: string) => void;
	onAddProduct: (category: SalesChannelCategoryRow) => void;
	onEditCategory: (category: SalesChannelCategoryRow) => void;
	onDeleteCategory: (category: SalesChannelCategoryRow) => void;
	onEditProduct: (row: SalesChannelCategoryGroupProductRow) => void;
	onDeleteProduct: (row: SalesChannelCategoryGroupProductRow) => void;
	canReorder: boolean;
	onReorderProducts: (
		category: SalesChannelCategoryRow,
		orderedRows: SalesChannelCategoryGroupProductRow[],
		movedProduct: SalesChannelCategoryGroupProductRow,
		newPosition: number,
	) => Promise<boolean>;
}) => {
	const { category, products } = node;
	const hasProducts = products.length > 0;

	const sensors = useSensors(
		useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates,
		}),
	);

	// Show the new order right away for a smooth drop; tie it to the product set it came from so a
	// refetch (new order key) drops the override and the server order wins — no reset effect needed.
	const productOrderKey = products
		.map((row) => row.salesChannelCategoryGroupProductID)
		.join(",");
	const [orderOverride, setOrderOverride] = useState<{
		orderKey: string;
		orderedProductIDs: string[];
	} | null>(null);

	const orderedProducts = useMemo(() => {
		if (!orderOverride || orderOverride.orderKey !== productOrderKey)
			return products;
		const productByID = new Map(
			products.map((row) => [row.salesChannelCategoryGroupProductID, row]),
		);
		const ordered = orderOverride.orderedProductIDs
			.map((id) => productByID.get(id))
			.filter((row): row is SalesChannelCategoryGroupProductRow => !!row);
		return ordered.length === products.length ? ordered : products;
	}, [orderOverride, productOrderKey, products]);

	const sortableIDs = orderedProducts.map(
		(row) => row.salesChannelCategoryGroupProductID,
	);

	// Move/insert: the dragged row lands on the drop target's slot, rows between shift one step.
	const handleDragEnd = async (event: DragEndEvent) => {
		const { active, over } = event;
		if (!over || active.id === over.id) return;

		const fromIndex = orderedProducts.findIndex(
			(row) => row.salesChannelCategoryGroupProductID === active.id,
		);
		const toIndex = orderedProducts.findIndex(
			(row) => row.salesChannelCategoryGroupProductID === over.id,
		);
		if (fromIndex < 0 || toIndex < 0) return;

		const reordered = arrayMove(orderedProducts, fromIndex, toIndex);
		setOrderOverride({
			orderKey: productOrderKey,
			orderedProductIDs: reordered.map(
				(row) => row.salesChannelCategoryGroupProductID,
			),
		});
		const applied = await onReorderProducts(
			category,
			reordered,
			orderedProducts[fromIndex],
			toIndex + 1,
		);
		// Confirm cancelled or save failed — snap back to the server order.
		if (!applied) setOrderOverride(null);
	};

	return (
		<div className="border-border/50 border-b last:border-b-0">
			{/* biome-ignore lint/a11y/useKeyWithClickEvents: row toggle is mirrored by the chevron button */}
			<div
				className={`group flex items-center gap-2 py-2 pr-2 transition-colors hover:bg-muted/50 ${
					hasProducts ? "cursor-pointer" : ""
				}`}
				style={{ paddingLeft: "4px" }}
				onClick={() => hasProducts && onToggle(category.salesChannelCategoryID)}
			>
				{hasProducts ? (
					<button
						type="button"
						aria-label={isOpen ? "Collapse" : "Expand"}
						className="flex h-5 w-5 shrink-0 items-center justify-center rounded text-muted-foreground hover:bg-muted"
						onClick={(e) => {
							e.stopPropagation();
							onToggle(category.salesChannelCategoryID);
						}}
					>
						<ChevronDownIcon
							className={`h-4 w-4 transition-transform ${
								isOpen ? "" : "-rotate-90"
							}`}
						/>
					</button>
				) : (
					<span className="h-5 w-5 shrink-0" />
				)}

				<ListIcon
					className={`h-4 w-4 shrink-0 ${
						hasProducts ? "text-primary" : "text-muted-foreground/50"
					}`}
				/>

				<span
					className={`truncate font-medium text-sm ${
						hasProducts ? "" : "text-muted-foreground"
					}`}
				>
					{category.salesChannelCategoryName}
				</span>

				<CategoryStatusBadge
					category={category}
					productCount={products.length}
				/>

				<div className="ml-auto flex shrink-0 items-center gap-2">
					{hasProducts ? (
						<Badge
							variant="outline"
							className="h-5 border-blue-200 bg-blue-50 px-1.5 font-normal text-[11px] text-blue-700"
						>
							{plural(products.length, "product")}
						</Badge>
					) : (
						<span className="text-muted-foreground/60 text-xs italic">
							No products
						</span>
					)}

					<div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
						<Can permissions="sales_channel_category_groups.create">
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="h-7 px-2 text-muted-foreground text-xs hover:text-foreground"
								onClick={(e) => {
									e.stopPropagation();
									onAddProduct(category);
								}}
							>
								<PlusIcon className="h-3.5 w-3.5" /> Add product
							</Button>
						</Can>
						<Can permissions="sales_channel_category_groups.edit">
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground"
								onClick={(e) => {
									e.stopPropagation();
									onEditCategory(category);
								}}
							>
								<EditIcon className="h-4 w-4" />
							</Button>
						</Can>
						<Can permissions="sales_channel_category_groups.delete">
							<Button
								type="button"
								variant="ghost"
								size="sm"
								className="h-7 w-7 p-0 text-destructive hover:text-destructive"
								onClick={(e) => {
									e.stopPropagation();
									onDeleteCategory(category);
								}}
							>
								<Trash2Icon className="h-4 w-4" />
							</Button>
						</Can>
					</div>
				</div>
			</div>

			{hasProducts && isOpen ? (
				<div className="mb-1 ml-[14px] border-border/60 border-l">
					<DndContext
						sensors={sensors}
						collisionDetection={closestCenter}
						onDragEnd={handleDragEnd}
					>
						<SortableContext
							items={sortableIDs}
							strategy={verticalListSortingStrategy}
							disabled={!canReorder}
						>
							{orderedProducts.map((product) => (
								<ProductRow
									key={product.salesChannelCategoryGroupProductID}
									product={product}
									showQty={showQty}
									canMarkUnavailable={canMarkUnavailable}
									isExcluded={excludedProductIDs.has(product.productID)}
									canReorder={canReorder}
									onEdit={onEditProduct}
									onDelete={onDeleteProduct}
									onMarkUnavailable={onMarkUnavailable}
								/>
							))}
						</SortableContext>
					</DndContext>
				</div>
			) : null}
		</div>
	);
};

export interface SalesChannelCategoryGroupTreeProps {
	groupID: string;
	categoryOptions: { value: string; label: string }[];
	onShowTable?: () => void;
	onChanged?: () => void;
}

interface VisibleNode {
	category: SalesChannelCategoryRow;
	products: SalesChannelCategoryGroupProductRow[];
}

export const SalesChannelCategoryGroupTree = ({
	groupID,
	categoryOptions,
	onShowTable,
	onChanged,
}: SalesChannelCategoryGroupTreeProps) => {
	const { confirm } = useDialogController();
	const { session } = useAuth();
	const { disable: disableItem, restore: restoreItem } =
		useBranchProductSalesChannelItemMutations();
	const excluded = useBranchProductSalesChannelItemList(
		groupID,
		BRANCH_PRODUCT_ITEM_TYPE_EXCLUDE,
	);
	const { channelIDs, branchesByChannelID } = useMenuGroupBindings(groupID);
	// One group binds one channel in practice; the first is the menu this tree shows.
	const channelID = channelIDs[0] ?? "";

	// Preselect the cashier's own branch, but only when this group covers it.
	const defaultBranch = useMemo(() => {
		const defaultBranchID = session?.meta?.defaultBranchID as
			| string
			| undefined;
		if (!defaultBranchID) return null;
		return branchesByChannelID.get(channelID)?.get(defaultBranchID) ?? null;
	}, [branchesByChannelID, channelID, session?.meta?.defaultBranchID]);

	const defaultBranchOption = useMemo(() => {
		if (!defaultBranch) return null;
		return {
			value: defaultBranch.branchID,
			label: defaultBranch.branchCode
				? `${defaultBranch.branchName} (${defaultBranch.branchCode})`
				: defaultBranch.branchName,
		};
	}, [defaultBranch]);

	const selectedBranchOptions = defaultBranchOption
		? [defaultBranchOption]
		: [];

	const [branchID, setBranchID] = useState("");
	const [branchLabel, setBranchLabel] = useState("");
	const [unavailableProduct, setUnavailableProduct] =
		useState<SalesChannelCategoryGroupProductRow | null>(null);

	// The default branch arrives with the bindings query, after the first render.
	useEffect(() => {
		if (!defaultBranchOption) return;
		setBranchID(String(defaultBranchOption.value));
		setBranchLabel(defaultBranchOption.label);
	}, [defaultBranchOption]);

	// Mark Unavailable targets the branch currently in view (the Stock-at-branch filter),
	// falling back to the cashier's default branch when none is picked.
	const markBranchOption = useMemo(
		() =>
			branchID
				? { value: branchID, label: branchLabel || branchID }
				: defaultBranchOption,
		[branchID, branchLabel, defaultBranchOption],
	);

	// Excluded rows are per branch, and the tree shows every product at once.
	useEffect(() => {
		excluded.setPagination({ pageIndex: 0, pageSize: 500 });
	}, [excluded.setPagination]);

	useEffect(() => {
		excluded.setFilters({ branchID: branchID || null });
	}, [branchID, excluded.setFilters]);

	const excludedProductIDs = useMemo(
		() => new Set(excluded.items.map((item) => item.productID)),
		[excluded.items],
	);
	// Branches each product is hidden at, so Restore works even when no stock branch is picked.
	const excludedBranchesByProductID = useMemo(() => {
		const byProduct = new Map<string, string[]>();
		for (const item of excluded.items) {
			if (!item.branchID) continue;
			const branches = byProduct.get(item.productID) ?? [];
			if (!branches.includes(item.branchID)) branches.push(item.branchID);
			byProduct.set(item.productID, branches);
		}
		return byProduct;
	}, [excluded.items]);
	const { tree, categoryCount, productCount, fetching, stockError, refetch } =
		useSalesChannelCategoryGroupTree(groupID, branchID);
	// Empty channel fans out over every store bound to this group, matching the detail header.
	const { load: loadBranch } = useMenuGroupBranchAutocomplete(groupID, "");

	const branchFetcher: AutocompleteFetcher = (params) =>
		loadBranch(params.page, params.pageSize, params.search);
	const categoryMutations = useSalesChannelCategoryMutations();
	const productMutations = useSalesChannelCategoryGroupProductMutations();
	const { load: loadProduct } = useProductAutocomplete();

	const productFetcher = (params: AutocompleteFetchParams) =>
		loadProduct(params.page, params.pageSize, params.search, params.moreFilter);

	const [categoryDialog, setCategoryDialog] =
		useState<CategoryDialogState>(null);
	const [productDialog, setProductDialog] = useState<ProductDialogState>(null);

	const [search, setSearch] = useState("");
	const [expanded, setExpanded] = useState<Set<string>>(new Set());

	const categoryIDsWithProducts = useMemo(
		() =>
			tree
				.filter((n) => n.products.length > 0)
				.map((n) => n.category.salesChannelCategoryID),
		[tree],
	);

	// Auto-expand every category for small trees; start collapsed for large ones.
	useEffect(() => {
		if (!categoryIDsWithProducts.length) return;
		setExpanded(
			categoryCount <= AUTO_EXPAND_LIMIT
				? new Set(categoryIDsWithProducts)
				: new Set(),
		);
	}, [categoryIDsWithProducts, categoryCount]);

	const visibleTree = useMemo<VisibleNode[]>(() => {
		const q = search.trim().toLowerCase();
		return tree
			.map((node): VisibleNode | null => {
				if (!q) return { category: node.category, products: node.products };
				const products = node.products.filter((p) => productMatches(p, q));
				if (products.length === 0) return null;
				return { category: node.category, products };
			})
			.filter((x): x is VisibleNode => x !== null);
	}, [tree, search]);

	const searching = !!search.trim();
	const effectiveExpanded = useMemo(() => {
		if (!searching) return expanded;
		return new Set(
			visibleTree
				.filter((x) => x.products.length > 0)
				.map((x) => x.category.salesChannelCategoryID),
		);
	}, [searching, expanded, visibleTree]);

	const toggle = (id: string) =>
		setExpanded((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});

	const expandAll = () => setExpanded(new Set(categoryIDsWithProducts));
	const collapseAll = () => setExpanded(new Set());

	const afterChange = () => {
		refetch();
		onChanged?.();
	};

	const submitCategory = async (values: CategoryFormValues) => {
		const payload = {
			salesChannelCategoryGroupID: groupID,
			salesChannelCategoryName: values.salesChannelCategoryName,
			displayOrder: Number(values.displayOrder) || 1,
			isActive: values.isActive,
			isInMenu: values.isInMenu,
		};
		const result =
			categoryDialog?.mode === "edit"
				? await categoryMutations.update({
						...payload,
						salesChannelCategoryID: categoryDialog.row.salesChannelCategoryID,
					})
				: await categoryMutations.create(payload);
		if (result.error) {
			toast.error(result.error.message);
			throw result.error;
		}
		toast.success(
			categoryDialog?.mode === "edit" ? "Category updated" : "Category added",
		);
		setCategoryDialog(null);
		afterChange();
	};

	// Restore needs no extra input, so it stays a confirm; excluding opens the
	// same dialog as the Unavailable tab, with the product already chosen.
	const toggleUnavailable = async (
		product: SalesChannelCategoryGroupProductRow,
	) => {
		if (!excludedProductIDs.has(product.productID)) {
			setUnavailableProduct(product);
			return;
		}

		// Use the selected stock branch, or every branch the item is hidden at when none is picked.
		const restoreBranchIDs = branchID
			? [branchID]
			: (excludedBranchesByProductID.get(product.productID) ?? []);
		if (restoreBranchIDs.length === 0) {
			toast.error("No branch to restore this item at");
			return;
		}

		const at = branchLabel ? ` at ${branchLabel}` : "";
		const ok = await confirm({
			title: `Restore ${productLabel(product)}?`,
			description: `The item returns to the channel menu${at}.`,
			confirmText: "Restore",
			cancelText: "Cancel",
		});
		if (!ok) return;

		for (const restoreBranchID of restoreBranchIDs) {
			const result = await restoreItem({
				branchID: restoreBranchID,
				productID: product.productID,
				salesChannelID: channelID,
				type: BRANCH_PRODUCT_ITEM_TYPE_EXCLUDE,
			});
			if (result.error) {
				toast.error(result.error.message);
				return;
			}
		}

		toast.success("Item restored");
		excluded.load();
		refetch();
		onChanged?.();
	};

	const submitUnavailable = async (values: UnavailableItemFormValues) => {
		const result = await disableItem({
			branchID: values.branchID,
			productID: values.productID,
			salesChannelID: channelID,
			salesChannelCategoryGroupID: groupID,
			type: BRANCH_PRODUCT_ITEM_TYPE_EXCLUDE,
			note: values.note,
		});

		if (result.error) {
			toast.error(result.error.message);
			throw result.error;
		}

		toast.success("Item marked unavailable");
		setUnavailableProduct(null);
		excluded.load();
		refetch();
		onChanged?.();
	};

	const deleteCategory = async (category: SalesChannelCategoryRow) => {
		const ok = await confirm({
			title: `Delete ${category.salesChannelCategoryName}?`,
			description: "This action cannot be undone.",
			destructive: true,
			confirmText: "Delete",
			cancelText: "Cancel",
		});
		if (!ok) return;
		const result = await categoryMutations.remove(
			category.salesChannelCategoryID,
		);
		if (result.error) {
			toast.error(result.error.message);
			return;
		}
		toast.success("Category removed");
		afterChange();
	};

	const reorderProducts = async (
		category: SalesChannelCategoryRow,
		orderedRows: SalesChannelCategoryGroupProductRow[],
		movedProduct: SalesChannelCategoryGroupProductRow,
		newPosition: number,
	): Promise<boolean> => {
		const ok = await confirm({
			title: "Move product?",
			description: `Move "${productLabel(movedProduct)}" to position ${newPosition} in ${category.salesChannelCategoryName}?`,
			confirmText: "Move",
			cancelText: "Cancel",
		});
		if (!ok) return false;

		// The whole category is renumbered so the shifted rows persist their new order too.
		const result = await productMutations.reorder(
			orderedRows.map((row, index) => ({
				salesChannelCategoryGroupProductID:
					row.salesChannelCategoryGroupProductID,
				displayOrder: index + 1,
			})),
		);
		if (result.error) {
			toast.error(result.error.message);
			refetch();
			return false;
		}
		toast.success("Products reordered");
		afterChange();
		return true;
	};

	const submitProduct = async (values: GroupProductFormValues) => {
		const payload = {
			salesChannelCategoryGroupID: groupID,
			salesChannelCategoryID: values.salesChannelCategoryID,
			// Product is locked on edit (disabled field submits no value); take it from the row.
			productID:
				productDialog?.mode === "edit"
					? productDialog.row.productID
					: values.productID,
			isActive: values.isActive,
			// Custom price is hidden from the UI for now — always pricelist.
			priceType: "pricelist",
			customPrice: 0,
			externalImageURL: values.menuImage?.[0]?.url || "",
			imageType: resolveMenuImageType(values.menuImage?.[0]?.url),
		};
		const result =
			productDialog?.mode === "edit"
				? await productMutations.update({
						...payload,
						salesChannelCategoryGroupProductID:
							productDialog.row.salesChannelCategoryGroupProductID,
					})
				: await productMutations.create(payload);
		if (result.error) {
			toast.error(result.error.message);
			throw result.error;
		}
		toast.success(
			productDialog?.mode === "edit" ? "Product updated" : "Product added",
		);
		setProductDialog(null);
		afterChange();
	};

	const deleteProduct = async (row: SalesChannelCategoryGroupProductRow) => {
		const ok = await confirm({
			title: `Remove ${productLabel(row)}?`,
			description: "This action cannot be undone.",
			destructive: true,
			confirmText: "Remove",
			cancelText: "Cancel",
		});
		if (!ok) return;
		const result = await productMutations.remove(
			row.salesChannelCategoryGroupProductID,
		);
		if (result.error) {
			toast.error(result.error.message);
			return;
		}
		toast.success("Product removed");
		afterChange();
	};

	const editProductDefaults =
		productDialog?.mode === "edit"
			? {
					productID: productDialog.row.productID,
					salesChannelCategoryID: productDialog.row.salesChannelCategoryID,
					priceType: productDialog.row.priceType,
					customPrice: productDialog.row.customPrice,
					menuImage: resolveMenuImagePrefill(productDialog.row),
					isActive: productDialog.row.isActive,
				}
			: productDialog?.mode === "create" && productDialog.categoryID
				? { salesChannelCategoryID: productDialog.categoryID }
				: undefined;

	return (
		<div className="flex flex-col gap-3">
			<div className="flex flex-wrap items-center justify-between gap-2">
				<div className="flex items-center gap-2">
					<ListTreeIcon className="h-5 w-5 text-primary" />
					<div>
						<p className="font-semibold text-sm leading-none">
							Categories → Products
						</p>
						<p className="mt-1 text-muted-foreground text-xs">
							{fetching
								? "Loading…"
								: `${plural(categoryCount, "category", "categories")} · ${plural(productCount, "product")}`}
						</p>
					</div>
				</div>
				<div className="flex items-center gap-1.5">
					{onShowTable ? (
						<Button
							type="button"
							variant="outline"
							size="sm"
							onClick={onShowTable}
						>
							<TableIcon className="h-4 w-4" />
							Show Table
						</Button>
					) : null}
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={expandAll}
						disabled={searching || !categoryIDsWithProducts.length}
					>
						<ChevronsUpDownIcon className="h-4 w-4" />
						Expand all
					</Button>
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={collapseAll}
						disabled={searching || !categoryIDsWithProducts.length}
					>
						<ChevronsDownUpIcon className="h-4 w-4" />
						Collapse all
					</Button>
				</div>
			</div>

			<div className="flex flex-wrap items-center gap-2">
				<div className="flex min-w-[260px] flex-1 flex-wrap items-center gap-2 sm:flex-nowrap">
					<div className="relative min-w-[180px] flex-1">
						<SearchIcon className="-translate-y-1/2 absolute top-1/2 left-2.5 h-4 w-4 text-muted-foreground" />
						<Input
							value={search}
							onChange={(e) => setSearch(e.target.value)}
							placeholder="Search product…"
							className="pl-8"
						/>
					</div>
					<div className="min-w-[180px] flex-1">
						<Autocomplete
							key={defaultBranchOption?.value ?? "no-default-branch"}
							fetcher={branchFetcher}
							mode="server"
							options={[]}
							placeholder="Stock at branch…"
							initialSelectedOptions={selectedBranchOptions}
							loadSelected={() => Promise.resolve(selectedBranchOptions)}
							defaultValue={defaultBranchOption?.value ?? null}
							onChange={(value, selected) => {
								setBranchID((value as string) || "");
								const option = Array.isArray(selected) ? selected[0] : selected;
								setBranchLabel(option?.label ?? "");
							}}
						/>
					</div>
				</div>
				<div className="ml-auto flex items-center gap-2">
					<Button
						type="button"
						variant="outline"
						size="sm"
						onClick={() => {
							refetch();
						}}
						disabled={fetching}
						title="Refresh"
					>
						<RefreshCwIcon
							className={`h-4 w-4 ${fetching ? "animate-spin" : ""}`}
						/>
						Refresh
					</Button>
					<Can permissions="sales_channel_category_groups.create">
						<Button
							type="button"
							size="sm"
							onClick={() => setCategoryDialog({ mode: "create" })}
						>
							<PlusIcon className="h-4 w-4" />
							Add category
						</Button>
					</Can>
				</div>
			</div>

			{stockError ? (
				<div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-xs">
					Products unavailable
				</div>
			) : null}

			<Separator />

			{/* Radix wraps the viewport content in a display:table node that ignores min-w-0,
			    so rows grow to their natural width and overflow on narrow screens; force it to block. */}
			<ScrollArea className="h-[60vh] pr-3 [&>[data-radix-scroll-area-viewport]>div]:!block">
				{fetching ? (
					<div className="flex flex-col gap-2 py-1">
						{[0, 1, 2, 3, 4, 5].map((i) => (
							<Skeleton
								key={i}
								className="h-7"
								style={{ width: `${90 - i * 8}%` }}
							/>
						))}
					</div>
				) : visibleTree.length ? (
					<div className="py-1">
						{visibleTree.map(({ category, products }) => (
							<CategoryRow
								key={category.salesChannelCategoryID}
								node={{ category, products }}
								isOpen={effectiveExpanded.has(category.salesChannelCategoryID)}
								showQty={Boolean(branchID)}
								canMarkUnavailable={Boolean(channelID)}
								onMarkUnavailable={toggleUnavailable}
								excludedProductIDs={excludedProductIDs}
								onToggle={toggle}
								onAddProduct={(c) =>
									setProductDialog({
										mode: "create",
										categoryID: c.salesChannelCategoryID,
									})
								}
								onEditCategory={(c) =>
									setCategoryDialog({ mode: "edit", row: c })
								}
								onDeleteCategory={deleteCategory}
								onEditProduct={(p) =>
									setProductDialog({ mode: "edit", row: p })
								}
								onDeleteProduct={deleteProduct}
								canReorder={!searching}
								onReorderProducts={reorderProducts}
							/>
						))}
					</div>
				) : (
					<div className="flex flex-col items-center justify-center gap-2 py-12 text-center">
						<ListTreeIcon className="h-8 w-8 text-muted-foreground/50" />
						<p className="text-muted-foreground text-sm">
							{searching
								? `No matches for “${search.trim()}”.`
								: "No categories to display."}
						</p>
					</div>
				)}
			</ScrollArea>

			<SalesChannelCategoryFormDialog
				open={!!categoryDialog}
				onOpenChange={(open) => !open && setCategoryDialog(null)}
				mode={categoryDialog?.mode ?? "create"}
				defaultValues={
					categoryDialog?.mode === "edit"
						? {
								salesChannelCategoryName:
									categoryDialog.row.salesChannelCategoryName,
								displayOrder: categoryDialog.row.displayOrder,
								isActive: categoryDialog.row.isActive,
								isInMenu: categoryDialog.row.isInMenu,
							}
						: undefined
				}
				onSubmit={submitCategory}
			/>

			<SalesChannelGroupProductFormDialog
				open={!!productDialog}
				onOpenChange={(open) => !open && setProductDialog(null)}
				mode={productDialog?.mode ?? "create"}
				categoryOptions={categoryOptions}
				productAutocompleteFetcher={productFetcher}
				initialProduct={
					productDialog?.mode === "edit"
						? {
								value: productDialog.row.productID,
								label: productLabel(productDialog.row),
							}
						: undefined
				}
				defaultValues={editProductDefaults}
				onSubmit={submitProduct}
			/>

			<UnavailableItemDialog
				open={!!unavailableProduct}
				onOpenChange={(open) => !open && setUnavailableProduct(null)}
				branchFetcher={branchFetcher}
				defaultBranchOption={markBranchOption}
				productFetcher={productFetcher}
				lockedProductOption={
					unavailableProduct
						? {
								value: unavailableProduct.productID,
								label: productLabel(unavailableProduct),
							}
						: null
				}
				onSubmit={submitUnavailable}
			/>
		</div>
	);
};
